import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { CreditCard, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Reusable RyftPay 3DS payment dialog.
 *
 * Mounts the RyftPay SDK card form, runs `attemptPayment()`, and on a successful
 * Approved / Captured status invokes `onSuccess` with the payment session result.
 *
 * Props:
 *   open               – boolean, dialog visibility
 *   onClose            – called when user cancels / dialog closes
 *   publicKey          – RyftPay publishable key (`pk_...`)
 *   clientSecret       – session client secret (`ps_...`)
 *   accountId          – sub-account id for platform fee sessions (optional)
 *   redirectUrl        – fallback hosted-3DS redirect (optional)
 *   amountLabel        – string shown to user (e.g. "€60 — 4 classes")
 *   customerPaymentMethods – iter-42 Phase 1: raw response from
 *                       `GET /api/payments/customer/methods` (i.e. Ryft's
 *                       `customers/{id}/payment-methods`). When provided and
 *                       non-empty, the SDK card form renders saved-card chips
 *                       so returning customers can pay in one click. When
 *                       missing or empty, the form behaves exactly as before
 *                       (plain card-entry, current proven behaviour).
 *   requireSaveCard    – iter-46ar: true when the payment session uses
 *                       `paymentType: Unscheduled` (membership ClassPass).
 *                       Ryft will 400 attempt-payment if the card isn't saved
 *                       on those sessions. Force-saves the card via SDK.
 *                       Default false (Standard sessions — single class, basket,
 *                       non-membership ClassPass — keep the checkbox optional).
 *   onSuccess          – async fn(result) invoked when payment is approved
 */
export const RyftPay3DSDialog = ({
  open,
  onClose,
  publicKey,
  clientSecret,
  accountId,
  redirectUrl,
  amountLabel,
  customerPaymentMethods,
  requireSaveCard = false,
  onSuccess,
}) => {
  const [cardFormInstance, setCardFormInstance] = useState(null);
  const [cardFormReady, setCardFormReady] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!open || !clientSecret || !publicKey) {
      setCardFormInstance(null);
      setCardFormReady(false);
      return;
    }
    if (!clientSecret.startsWith('ps_')) {
      console.error('Invalid clientSecret:', clientSecret);
      return;
    }

    let mounted = true;

    const mountForm = async () => {
      await new Promise(r => setTimeout(r, 300));
      if (!mounted) return;

      let attempts = 0;
      while (!window.Ryft || typeof window.Ryft.createController !== 'function') {
        attempts++;
        if (attempts > 30 || !mounted) return;
        await new Promise(r => setTimeout(r, 100));
      }

      const container = document.getElementById('ryft-3ds-shared-container');
      if (!container || !mounted) return;
      container.innerHTML = '';

      const { createController, createCardForm } = window.Ryft;
      try {
        const cfg = { publicKey, clientSecret };
        if (accountId) cfg.accountId = accountId;
        const controller = createController(cfg);

        // Iter-44 fix: per Ryft docs, `details` must be the full `{ items: [...] }`
        // response from `customerGetPaymentMethods`. A bare array silently disables chips.
        const customerCardDetails = customerPaymentMethods;
        const hasSavedCards =
          customerCardDetails &&
          Array.isArray(customerCardDetails.items) &&
          customerCardDetails.items.length > 0;
        console.log('RyftPay3DSDialog mounting cardForm with', hasSavedCards ? customerCardDetails.items.length : 0, 'saved cards');
        const cardFormConfig = {
          customerPaymentMethods: {
            // Iter-70: 'Save this card' checkbox is no longer shown at
            // checkout. Cards must be saved via Profile > Billing so the
            // proper CIT verifyAccount=true flow runs. This gives us a
            // clean MIT-eligible card for POS charging + membership
            // auto-renew. The existing saved cards still appear as
            // one-click chips inside the SDK form when present.
            allowStorage: { enabled: false },
            ...(hasSavedCards ? { details: customerCardDetails } : {}),
          },
          validationMode: 'onBlur',
        };
        const cardForm = createCardForm(controller, cardFormConfig);

        cardForm.on('validationChange', (event) => {
          if (mounted) setCardFormReady(event.isValid);
        });
        // When user picks a saved card chip, enable Pay immediately.
        cardForm.on('paymentMethodChange', (event) => {
          console.log('RyftPay3DSDialog paymentMethodChange:', event);
          if (mounted) setCardFormReady(true);
        });
        cardForm.on('error', (err) => {
          // Iter-46ar: surface the full SDK error to our backend log so we can
          // diagnose Ryft 400s on production without needing browser DevTools.
          // Best-effort — never throw.
          console.error('Card form error:', err);
          const payload = {
            kind: 'cardform_error',
            message: err?.message || err?.code || String(err) || 'unknown',
            error: (() => {
              try { return JSON.parse(JSON.stringify(err)); } catch { return null; }
            })(),
            client_secret_prefix: typeof clientSecret === 'string' ? clientSecret.slice(0, 8) : null,
            account_id: accountId || null,
            amount_label: amountLabel || null,
          };
          try {
            const url = (process.env.REACT_APP_BACKEND_URL || '') + '/api/payments/sdk-error';
            const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
            // sendBeacon is fire-and-forget — won't block the SDK or fail loudly.
            if (navigator.sendBeacon) navigator.sendBeacon(url, blob);
          } catch (_) { /* swallow */ }

          // The SDK renders its own inline error banner with the format
          // "[Ryft card-form Error] API Error: 400 - <uuid>" which leaks raw
          // diagnostics to end users. Hide any error nodes that match this
          // pattern inside our container.
          try {
            const cont = document.getElementById('ryft-3ds-shared-container');
            if (cont) {
              cont.querySelectorAll('*').forEach((el) => {
                const t = el.textContent || '';
                if (/\[Ryft\s+card-form\s+Error\]|API Error:\s*\d+\s*-\s*[a-f0-9-]{20,}/i.test(t)) {
                  el.style.display = 'none';
                }
              });
            }
          } catch (_) { /* swallow */ }
        });

        cardForm.mount(container);
        if (mounted) setCardFormInstance(cardForm);
      } catch (e) {
        console.error('Error creating payment form:', e);
        if (mounted) toast.error('Failed to load payment form: ' + e.message);
      }
    };

    mountForm();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientSecret, publicKey, accountId, customerPaymentMethods, requireSaveCard]);

  const handlePay = async () => {
    if (!cardFormInstance) {
      toast.error('Payment form not ready. Please wait or try again.');
      return;
    }
    setProcessing(true);

    try {
      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }

      const result = await cardFormInstance.attemptPayment();
      const status = result?.paymentSession?.status;

      if (status === 'Approved' || status === 'Captured') {
        await onSuccess?.(result);
      } else if (status === 'Declined' || status === 'Failed') {
        toast.error(result?.userFacingErrorMessage || 'Payment was declined.');
      } else {
        toast.error(result?.userFacingErrorMessage || `Payment status: ${status}. Please try again.`);
      }
    } catch (e) {
      console.error('Payment error:', e);
      // Iter-46ar: capture richer info for diagnostics on production.
      try {
        const url = (process.env.REACT_APP_BACKEND_URL || '') + '/api/payments/sdk-error';
        const payload = {
          kind: 'attempt_payment_throw',
          message: e?.message || String(e),
          code: e?.code || null,
          error: (() => {
            try { return JSON.parse(JSON.stringify(e)); } catch { return null; }
          })(),
          client_secret_prefix: typeof clientSecret === 'string' ? clientSecret.slice(0, 8) : null,
          account_id: accountId || null,
          amount_label: amountLabel || null,
        };
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        if (navigator.sendBeacon) navigator.sendBeacon(url, blob);
      } catch (_) { /* swallow */ }

      // Iter-46an + iter-46ar: the SDK's default surfaced message is either
      //   "API Error: 400 - <uuid>"            (older SDK)
      //   "[Ryft card-form Error] API Error: 400 - <uuid>"  (newer SDK)
      // Both are useless to a customer. Translate either to a friendly toast
      // and fall back to the request id only when nothing else is available
      // so a studio admin can still grep the logs.
      const friendly =
        e?.userFacingErrorMessage ||
        e?.displayError ||
        e?.code ||
        e?.error?.message ||
        e?.message;
      const looksLikeReqId =
        typeof friendly === 'string' &&
        /API Error:\s*\d+\s*-\s*[a-f0-9-]{20,}/i.test(friendly);
      if (looksLikeReqId) {
        toast.error(
          'Your bank declined the payment or the card was not accepted. ' +
            'Please double-check the card details and try again, or use a different card.'
        );
      } else {
        toast.error(friendly || 'Payment failed. Please try again.');
      }
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()} modal={false}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="ryft-3ds-dialog"
        // Iter-43 fix: don't dismiss the dialog when the user interacts with
        // the Ryft SDK's 3DS challenge iframe (which renders outside our DOM).
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary" />
            Complete Your Payment
          </DialogTitle>
          <DialogDescription>
            Please enter your card details. Your bank may require additional verification.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {amountLabel && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Total:</p>
              <p className="font-medium">{amountLabel}</p>
            </div>
          )}
          <div id="ryft-3ds-shared-container" className="min-h-[250px] border rounded-lg p-3"></div>
          {/* Iter-70: 'Save this card' has been removed from checkout. Direct
              users to Profile > Billing to save a card for faster future
              checkouts + membership auto-renew. */}
          <p
            className="text-xs text-muted-foreground text-center"
            data-testid="ryft-save-card-hint"
          >
            To save a card for faster future checkouts, add it in{' '}
            <a href="/billing" className="underline hover:text-primary">
              Profile &rsaquo; Billing
            </a>
            .
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={processing}>Cancel</Button>
          <Button
            onClick={handlePay}
            disabled={processing || !cardFormReady}
            data-testid="ryft-pay-now-btn"
          >
            {processing ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
            ) : !cardFormReady ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
            ) : (
              'Pay Now'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RyftPay3DSDialog;
